import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { KeyRound, Trash2, ShieldCheck, Plus, Blocks } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { normalizeAvisoPhone } from '@conversia/shared-types';
import * as settingsApi from '@/api/settings.api';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/Tabs';
import { Card, CardBody, CardHeader, CardTitle, CardDescription } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Spinner } from '@/components/ui/Spinner';
import { EmptyState } from '@/components/ui/EmptyState';
import { PageHeader } from '@/components/common/PageHeader';
import { useToast } from '@/hooks/useToast';
import { useAuthStore } from '@/store/auth.store';
import { hasRole } from '@conversia/shared-types';

const TIMEZONES = [
  'America/Santo_Domingo',
  'America/New_York',
  'America/Mexico_City',
  'America/Bogota',
  'America/Argentina/Buenos_Aires',
  'Europe/Madrid',
  'UTC',
];

function findValue(list: settingsApi.Setting[] | undefined, key: string): string {
  const s = list?.find((x) => x.key === key);
  return s && typeof s.value === 'string' ? s.value : '';
}

function GeneralTab() {
  const qc = useQueryClient();
  const toast = useToast();
  const { data, isLoading } = useQuery({ queryKey: ['settings', 'list'], queryFn: settingsApi.listSettings });
  const [name, setName] = useState('');
  const [tz, setTz] = useState('America/Santo_Domingo');

  useEffect(() => {
    if (data) {
      setName(findValue(data, 'general.installationName') || 'Conversia');
      setTz(findValue(data, 'general.timezone') || 'America/Santo_Domingo');
    }
  }, [data]);

  const save = useMutation({
    mutationFn: async () => {
      await settingsApi.saveSetting('general.installationName', name);
      await settingsApi.saveSetting('general.timezone', tz);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['settings', 'list'] });
      toast.success('Configuración guardada');
    },
    onError: () => toast.error('No se pudo guardar'),
  });

  if (isLoading) return <Spinner />;

  return (
    <Card>
      <CardHeader>
        <CardTitle>General</CardTitle>
        <CardDescription>Identidad y zona horaria de esta instalación.</CardDescription>
      </CardHeader>
      <CardBody className="space-y-4">
        <Input label="Nombre de la instalación" value={name} onChange={(e) => setName(e.target.value)} />
        <Select
          label="Zona horaria"
          value={tz}
          onChange={(e) => setTz(e.target.value)}
          options={TIMEZONES.map((t) => ({ value: t, label: t }))}
        />
        <div>
          <Button onClick={() => save.mutate()} loading={save.isPending}>
            Guardar cambios
          </Button>
        </div>
      </CardBody>
    </Card>
  );
}

function HorarioTab() {
  const qc = useQueryClient();
  const toast = useToast();
  const { data, isLoading } = useQuery({
    queryKey: ['settings', 'agent-schedule'],
    queryFn: settingsApi.getAgentSchedule,
  });
  const [desde, setDesde] = useState(7);
  const [hasta, setHasta] = useState(22);
  const [activo, setActivo] = useState(true);
  const [tz, setTz] = useState('America/Santo_Domingo');

  useEffect(() => {
    if (data) {
      setDesde(data.responder_desde);
      setHasta(data.responder_hasta);
      setActivo(data.horario_activo);
      setTz(data.timezone || 'America/Santo_Domingo');
    }
  }, [data]);

  const save = useMutation({
    mutationFn: () =>
      settingsApi.saveAgentSchedule({
        responder_desde: desde,
        responder_hasta: hasta,
        horario_activo: activo,
        timezone: tz,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['settings', 'agent-schedule'] });
      toast.success('Horario guardado');
    },
    onError: () => toast.error('No se pudo guardar el horario'),
  });

  // Ventana incoherente (hasta <= desde) no se guarda: aviso claro en vez de
  // dejar al asistente mudo sin explicación.
  function guardarHorario() {
    if (activo && hasta <= desde) {
      toast.error('La hora "hasta" debe ser mayor que la hora "desde"');
      return;
    }
    save.mutate();
  }

  if (isLoading) return <Spinner />;

  const hourOpts = (from: number, to: number) =>
    Array.from({ length: to - from + 1 }, (_, i) => from + i).map((h) => ({
      value: String(h),
      label: `${String(h).padStart(2, '0')}:00`,
    }));

  return (
    <Card>
      <CardHeader>
        <CardTitle>Horario del agente</CardTitle>
        <CardDescription>
          Ventana en la que el agente responde automáticamente. Fuera de ella, los
          mensajes quedan en la bandeja para un humano. Zona horaria del horario.
        </CardDescription>
      </CardHeader>
      <CardBody className="space-y-4">
        <label className="flex cursor-pointer items-center justify-between gap-4">
          <div>
            <p className="text-sm font-medium text-slate-800 dark:text-slate-100">Horario activo</p>
            <p className="text-xs text-slate-400">Si se apaga, el agente responde 24/7.</p>
          </div>
          <input
            type="checkbox"
            checked={activo}
            onChange={(e) => setActivo(e.target.checked)}
            className="h-5 w-5 accent-brand-600"
          />
        </label>
        <div className="grid grid-cols-2 gap-3">
          <Select
            label="Responder desde"
            value={String(desde)}
            onChange={(e) => setDesde(Number(e.target.value))}
            options={hourOpts(0, 23)}
            disabled={!activo}
          />
          <Select
            label="Responder hasta (excl.)"
            value={String(hasta)}
            onChange={(e) => setHasta(Number(e.target.value))}
            options={hourOpts(1, 24)}
            disabled={!activo}
          />
        </div>
        <Select
          label="Zona horaria"
          value={tz}
          onChange={(e) => setTz(e.target.value)}
          options={TIMEZONES.map((t) => ({ value: t, label: t }))}
        />
        <div>
          <Button onClick={guardarHorario} loading={save.isPending}>
            Guardar horario
          </Button>
        </div>
      </CardBody>
    </Card>
  );
}

function ByokTab() {
  const qc = useQueryClient();
  const toast = useToast();
  const { data: keys, isLoading } = useQuery({
    queryKey: ['settings', 'api-keys'],
    queryFn: () => settingsApi.getApiKeys(),
  });
  const [keyValue, setKeyValue] = useState('');
  const [label, setLabel] = useState('');

  const save = useMutation({
    mutationFn: () => settingsApi.saveApiKey({ key: keyValue, label: label || undefined }),
    onSuccess: () => {
      setKeyValue('');
      setLabel('');
      void qc.invalidateQueries({ queryKey: ['settings', 'api-keys'] });
      toast.success('API key guardada');
    },
    onError: () => toast.error('No se pudo guardar la API key'),
  });

  const remove = useMutation({
    mutationFn: (id: string) => settingsApi.deleteApiKey(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['settings', 'api-keys'] });
      toast.info('API key eliminada');
    },
  });

  const test = useMutation({
    mutationFn: (id: string) => settingsApi.testApiKey(id),
    onSuccess: (res) =>
      res.valid ? toast.success('La API key es válida') : toast.error('La API key no es válida'),
    onError: () => toast.error('No se pudo verificar la API key'),
  });

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Clave de IA (Anthropic)</CardTitle>
          <CardDescription>
            Usa tu propia API key de Anthropic (Claude). Se almacena cifrada en el servidor.
          </CardDescription>
        </CardHeader>
        <CardBody className="space-y-4">
          <Input
            label="API key"
            type="password"
            placeholder="sk-ant-…"
            value={keyValue}
            onChange={(e) => setKeyValue(e.target.value)}
          />
          <Input
            label="Etiqueta (opcional)"
            placeholder="Producción"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
          />
          <div>
            <Button onClick={() => save.mutate()} loading={save.isPending} disabled={keyValue.length < 8}>
              <Plus className="h-4 w-4" /> Guardar API key
            </Button>
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Keys configuradas</CardTitle>
        </CardHeader>
        <CardBody>
          {isLoading ? (
            <Spinner />
          ) : !keys || keys.length === 0 ? (
            <EmptyState icon={KeyRound} title="Sin API keys" description="Agrega una API key para habilitar los agentes." />
          ) : (
            <ul className="divide-y divide-surface-border dark:divide-slate-800">
              {keys.map((k) => (
                <li key={k.id} className="flex items-center gap-3 py-3">
                  <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-brand-100 text-brand-700 dark:bg-brand-900/40 dark:text-brand-300">
                    <KeyRound className="h-4 w-4" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-slate-800 dark:text-slate-100">
                      {k.label || k.provider}
                    </p>
                    <p className="truncate font-mono text-xs text-slate-400">{k.masked}</p>
                  </div>
                  <Badge variant="brand">{k.provider}</Badge>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => test.mutate(k.id)}
                    loading={test.isPending && test.variables === k.id}
                  >
                    <ShieldCheck className="h-4 w-4" /> Probar
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => remove.mutate(k.id)}
                    loading={remove.isPending && remove.variables === k.id}
                  >
                    <Trash2 className="h-4 w-4 text-danger" />
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </CardBody>
      </Card>
    </div>
  );
}

/** Preferencias locales de avisos, persistidas de verdad en localStorage. */
const NOTIF_PREFS_KEY = 'conversia.notifs.v1';

function readNotifPrefs(): { email: boolean; sound: boolean } {
  try {
    const raw = localStorage.getItem(NOTIF_PREFS_KEY);
    if (raw) return { email: true, sound: false, ...(JSON.parse(raw) as object) };
  } catch {
    // localStorage bloqueado o JSON corrupto: defaults
  }
  return { email: true, sound: false };
}

function NotificationsTab() {
  const [prefs, setPrefs] = useState(readNotifPrefs);
  const update = (patch: Partial<{ email: boolean; sound: boolean }>) => {
    const next = { ...prefs, ...patch };
    setPrefs(next);
    try {
      localStorage.setItem(NOTIF_PREFS_KEY, JSON.stringify(next));
    } catch {
      // sin localStorage: la preferencia vive solo esta sesión
    }
  };
  const email = prefs.email;
  const sound = prefs.sound;
  const setEmail = (v: boolean) => update({ email: v });
  const setSound = (v: boolean) => update({ sound: v });
  const Row = ({
    label,
    desc,
    checked,
    onChange,
  }: {
    label: string;
    desc: string;
    checked: boolean;
    onChange: (v: boolean) => void;
  }) => (
    <label className="flex cursor-pointer items-center justify-between gap-4 py-3">
      <div>
        <p className="text-sm font-medium text-slate-800 dark:text-slate-100">{label}</p>
        <p className="text-xs text-slate-400">{desc}</p>
      </div>
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="h-5 w-5 accent-brand-600"
      />
    </label>
  );
  return (
    <Card>
      <CardHeader>
        <CardTitle>Notificaciones</CardTitle>
        <CardDescription>Preferencias locales de avisos (se guardan en este navegador).</CardDescription>
      </CardHeader>
      <CardBody className="divide-y divide-surface-border dark:divide-slate-800">
        <Row
          label="Notificaciones por email"
          desc="Recibe un correo cuando una conversación necesita atención humana."
          checked={email}
          onChange={setEmail}
        />
        <Row
          label="Sonido de mensajes nuevos"
          desc="Reproduce un sonido al recibir un mensaje en tiempo real."
          checked={sound}
          onChange={setSound}
        />
      </CardBody>
    </Card>
  );
}

/** Avisos al dueño/soporte (operaciones, PASO 4): número que recibe las alertas. */
function OperacionesTab() {
  const qc = useQueryClient();
  const toast = useToast();
  const { data, isLoading } = useQuery({
    queryKey: ['settings', 'ops-alerts'],
    queryFn: settingsApi.getOwnerAlerts,
  });
  const [phone, setPhone] = useState('');
  const [phoneError, setPhoneError] = useState<string | null>(null);

  useEffect(() => {
    if (data) setPhone(data.owner_phone ?? '');
  }, [data]);

  const save = useMutation({
    mutationFn: (ownerPhone: string) => settingsApi.saveOwnerAlerts({ owner_phone: ownerPhone }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['settings', 'ops-alerts'] });
      toast.success('Número del dueño guardado');
    },
    onError: () => toast.error('No se pudo guardar'),
  });

  // Código de país obligatorio (el bug del vacío): validar ANTES de guardar.
  // Vacío es válido: significa "sin avisos".
  function guardar() {
    let valor = '';
    if (phone.replace(/\D/g, '') !== '') {
      const normalized = normalizeAvisoPhone(phone);
      if (!normalized.ok) {
        setPhoneError(normalized.error);
        return;
      }
      valor = normalized.phone;
      setPhone(valor);
    }
    setPhoneError(null);
    save.mutate(valor);
  }

  if (isLoading) return <Spinner />;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Número que recibe los avisos</CardTitle>
        <CardDescription>
          WhatsApp que recibe los avisos internos (SLA vencido, no completado, lead caliente,
          reporte diario…) cuando un aviso no tiene números propios en Instalación → Mensajes.
          Formato con código de país (ej. 18295550000); a un número de RD de 10 dígitos el
          sistema le antepone el 1 solo. Vacío = no se envían avisos.
        </CardDescription>
      </CardHeader>
      <CardBody className="space-y-4">
        <Input
          label="Número del dueño / soporte"
          placeholder="18295550000"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          error={phoneError ?? undefined}
        />
        <div>
          <Button onClick={guardar} loading={save.isPending}>
            Guardar cambios
          </Button>
        </div>
      </CardBody>
    </Card>
  );
}

export default function Settings() {
  const navigate = useNavigate();
  // BYOK (API keys) es superficie reservada del OWNER (F2): la API responde
  // 403 a un ADMIN — acá solo se evita mostrarle una pestaña rota.
  const role = useAuthStore((s) => s.user?.role);
  const isOwner = role ? hasRole(role, 'OWNER') : false;

  return (
    <div className="space-y-6 p-6">
      <PageHeader title="Configuración" helpKey="settings" description="Ajustes generales, clave de IA y notificaciones." />

      {/*
        QUÉ SABE HACER EL NEGOCIO — el paso 5 del alta de cliente.

        Vive acá y NO dentro de un módulo a propósito: si colgara de uno
        («Catálogo», por ejemplo), apagar ese módulo se llevaría por delante la
        única pantalla desde la que se puede volver a encenderlo. Un candado
        que se cierra con la llave adentro.
      */}
      <Card>
        <CardBody className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="font-medium text-slate-900">Qué sabe hacer este negocio</p>
            <p className="text-sm text-slate-500">
              Encendé o apagá inventario, ventas, finanzas y lo demás. Un módulo apagado deja de
              existir para este negocio.
            </p>
          </div>
          <Button variant="outline" onClick={() => navigate('/ajustes/modulos')}>
            <Blocks className="h-4 w-4" />
            Abrir
          </Button>
        </CardBody>
      </Card>
      <Tabs defaultValue="general">
        <TabsList>
          <TabsTrigger value="general">General</TabsTrigger>
          <TabsTrigger value="horario">Horario</TabsTrigger>
          <TabsTrigger value="operaciones">Avisos</TabsTrigger>
          {isOwner && <TabsTrigger value="byok">Clave de IA</TabsTrigger>}
          <TabsTrigger value="notifications">Notificaciones</TabsTrigger>
        </TabsList>
        <TabsContent value="general">
          <GeneralTab />
        </TabsContent>
        <TabsContent value="horario">
          <HorarioTab />
        </TabsContent>
        <TabsContent value="operaciones">
          <OperacionesTab />
        </TabsContent>
        {isOwner && (
          <TabsContent value="byok">
            <ByokTab />
          </TabsContent>
        )}
        <TabsContent value="notifications">
          <NotificationsTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}
